import {Avatar, Space, Tag, Tooltip} from 'antd';
import React from 'react';
import NameWithLink from '../../components/NameWithLink';
import StatusMark from '../../components/StatusMark';
import StatusLine from '../../components/StatusLine';
import {budgetProcurementTypeConstants, budgetStatusConstants} from '../../constants';
import {textHelpers} from './text.helpers';
import {OptionsList} from './form.helpers';
import {colorsStyles} from '../styles';
import routes from '../../routes/routes.json';

const budgetStatuses = {
  [budgetStatusConstants.CLOSED]: {
    key: 'budgetStatuses.closed',
    color: '#000'
  },
  [budgetStatusConstants.DRAFT]: {
    key: 'budgetStatuses.draft',
    color: '#722ED1'
  },
  [budgetStatusConstants.IN_REVIEW]: {
    key: 'budgetStatuses.inReview',
    color: '#FA8C16'
  },
  [budgetStatusConstants.REJECTED]: {
    key: 'budgetStatuses.rejected',
    color: '#FF4D4F'
  },
  [budgetStatusConstants.ACTIVE]: {
    key: 'budgetStatuses.inBudget',
    color: '#52C41A'
  },
  [budgetStatusConstants.APPROVED]: {
    key: 'budgetStatuses.approved',
    color: '#52C41A'
  },
  [budgetStatusConstants.OVER_BUDGET]: {
    key: 'budgetStatuses.overBudget',
    color: '#FA8C16'
  },
};

/**
 * Key constants used to index into budgetSpendingData.
 * @type {{INVOICED: string, SPENT: string}}
 * @example
 * const key = budgetSpendingKeys.INVOICED; // 'invoiced'
 */
export const budgetSpendingKeys = {
  INVOICED: 'invoiced',
  SPENT: 'spend',
}

/**
 * Display metadata (amount key, chart color, label key) for each budget spending category.
 * @type {Object.<string, {amountKey: string, color: string, labelKey: string}>}
 * @example
 * const { color } = budgetSpendingData[budgetSpendingKeys.SPENT]; // '#515A6B'
 */
export const budgetSpendingData = {
  [budgetSpendingKeys.INVOICED]: {
    amountKey: 'invoiced_amount',
    color: '#B37FEB',
    labelKey: 'invoiced',
  },
  [budgetSpendingKeys.SPENT]: {
    amountKey: 'spent_amount',
    color: '#515A6B',
    labelKey: 'spent',
  },
}

/**
 * Returns a filtered list of budget status options for the given view type.
 * @param {'active'|'requests'} type - View type determining which statuses are included.
 * @returns {Array<{value: string, label: string, image: React.ReactElement}>} Filtered status options with status line images.
 * @example
 * const options = BudgetStatusesOptions('active');
 */
export const BudgetStatusesOptions = (type) => {
  const allowedStatuses = {
    'active': budgetHelpers.getActiveOrClosedBudgetStatuses(),
    'requests': budgetHelpers.getRequestedBudgetStatuses({excludeApproved: true})
  }[type] || [];

  const statuses = Object.keys(budgetStatuses).reduce((acc, value) => {
    acc[value] = budgetStatuses[value]?.key;
    return acc;
  }, {});

  return OptionsList(statuses).map(option => {
    let color = budgetStatuses[option.value]?.color;
    return {
      ...option,
      image: <StatusLine color={color} />
    }
  }).filter(status => allowedStatuses.includes(status.value));
}

/**
 * Collection of utility helpers for budget display, grouping, status, and navigation.
 * @namespace budgetHelpers
 * @example
 * import { budgetHelpers } from './budget.helpers';
 */
export const budgetHelpers = {
  /**
   * Returns the list of status constants a budget must have to be selectable for a new attachment (expense, invoice, payment).
   * @returns {string[]} Array of selectable budget status constants.
   * @example
   * budgetHelpers.getActiveBudgetStatuses(); // ['active', 'over_budget']
   */
  getActiveBudgetStatuses: () => [budgetStatusConstants.ACTIVE, budgetStatusConstants.OVER_BUDGET],
  /**
   * Returns the list of status constants for budget requests that have exited the approval workflow (shown on the Active Budgets page).
   * @returns {string[]} Array of workflow-resolved budget status constants.
   * @example
   * budgetHelpers.getActiveOrClosedBudgetStatuses(); // ['active', 'closed', 'over_budget']
   */
  getActiveOrClosedBudgetStatuses: () => [budgetStatusConstants.ACTIVE, budgetStatusConstants.CLOSED, budgetStatusConstants.OVER_BUDGET],
  /**
   * Returns the list of status constants for budget requests, optionally excluding APPROVED.
   * @param {Object} [params={}] - Parameters object.
   * @param {boolean} [params.excludeApproved=false] - When true, removes APPROVED from the returned list.
   * @returns {string[]} Array of budget request status constants.
   * @example
   * budgetHelpers.getRequestedBudgetStatuses({ excludeApproved: true });
   */
  getRequestedBudgetStatuses: ({excludeApproved = false} = {}) => {
    const statuses = [
      budgetStatusConstants.DRAFT,
      budgetStatusConstants.IN_REVIEW,
      budgetStatusConstants.REJECTED,
      budgetStatusConstants.APPROVED
    ];
    return excludeApproved ? statuses.filter(s => s !== budgetStatusConstants.APPROVED) : statuses;
  },
  /**
   * Groups a flat list of budget rows by budget_id, inserting a summary group row before each budget's rows.
   * @param {Array<Object>} [budgets=[]] - Flat list of budget row objects.
   * @returns {Array<Object>} Interleaved list of group header objects and individual budget rows.
   * @example
   * budgetHelpers.getGroupedBudgetsByBudget(budgetRows);
   */
  getGroupedBudgetsByBudget: (budgets = []) => {
    const groupedBudgets = [];
    const budgetKeyName = 'budget_id';
    budgets.forEach(budget => {
      const budgetId = budget[budgetKeyName];
      if (budgetId) {
        if (!groupedBudgets.find(b => b.budget_id === budgetId)) {
          groupedBudgets.push({
            id: `${budgetId}-grouped`,
            isGrouped: true,
            budget_id: budgetId,
            name: budget?.budget_name || 'Unnamed Budget',
            expanded_ids: budgets.filter(b => b[budgetKeyName] === budgetId).map(({id}) => id) || [],
          });
        }
      }
      groupedBudgets.push(budget);
    });

    return groupedBudgets;
  },
  /**
   * Enriches grouped budget rows with limit, utilization, and available amount data from the budgets list.
   * @param {Object} [params={}] - Parameters object.
   * @param {Array<Object>} [params.rows=[]] - Rows produced by getGroupedBudgetsByBudget.
   * @param {Array<Object>} [params.budgets=[]] - Budget summary objects keyed by id.
   * @returns {Array<Object>} Rows with utilization, limit_amount, and available_amount populated for group rows.
   * @example
   * budgetHelpers.getGroupedActiveBudgets({ rows: groupedRows, budgets });
   */
  getGroupedActiveBudgets: ({rows = [], budgets = []} = {}) => {
    return (rows || []).map(row => {
      if (!row.isGrouped) return row;
      const budget = budgets.find(b => b.id === row.budget_id);
      const limitAmount = budget?.limit_amount || 0;
      const utilizedAmount = budget?.utilized_amount || 0;
      const availableAmount = limitAmount - utilizedAmount;
      const utilizationPercentage = limitAmount > 0 ? Math.round((utilizedAmount / limitAmount) * 100) : 0;
      return {
        ...row,
        available_amount: availableAmount,
        name: budget?.name || row?.name,
        limit_amount: limitAmount,
        reported_by: budget?.reported_by,
        utilization_percentage: utilizationPercentage
      };
    });
  },
  /**
   * Returns an Avatar element with a Tooltip showing the user's full name or email.
   * @param {Object|null} user - User object with full_name, email, and optional logo.
   * @returns {React.ReactElement|null} Avatar with Tooltip, or null if no user.
   * @example
   * budgetHelpers.getOwner({ full_name: 'Jane Doe', email: 'jane@example.com' });
   */
  getOwner: (user) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <Tooltip title={name}>
        <Avatar src={user?.logo || undefined} size={24} style={{fontSize: 12}}>
          {textHelpers.getInitials(name)}
        </Avatar>
      </Tooltip>
    )
  },
  /**
   * Returns a Space element containing an Avatar and the user's full name or email.
   * @param {Object|null} user - User object with full_name, email, and optional logo.
   * @returns {React.ReactElement|null} Space with Avatar and name, or null if no user.
   * @example
   * budgetHelpers.getOwnerWithName({ full_name: 'Jane Doe', email: 'jane@example.com' });
   */
  getOwnerWithName: (user) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <Space size='small'>
        <Avatar src={user?.logo} size={24} style={{fontSize: 12}}>
          {textHelpers.getInitials(name)}
        </Avatar>
        <span>{name}</span>
      </Space>
    )
  },
  /**
   * Returns the display name for a budget, prefixed with its procurement number when available.
   * @param {Object} budget - Budget object with name and optional bi_number or po_number.
   * @returns {string} Budget display name, e.g. 'BI-001 / My Budget'.
   * @example
   * budgetHelpers.getBudgetName({ name: 'My Budget', procurement_type: 'budget_item', bi_number: 'BI-001' });
   */
  getBudgetName: (budget) => {
    const name = budget?.name;
    const number = budgetHelpers.getBudgetNumber(budget);
    return number ? `${number} / ${name}` : name;
  },
  /**
   * Returns the procurement number (bi_number or po_number) for the budget based on its procurement type.
   * @param {Object} budget - Budget object with procurement_type, bi_number, and po_number.
   * @returns {string|null} The procurement number or null if procurement type is unrecognised.
   * @example
   * budgetHelpers.getBudgetNumber({ procurement_type: 'purchase_order', po_number: 'PO-042' }); // 'PO-042'
   */
  getBudgetNumber: (budget) => ({
    [budgetProcurementTypeConstants.BUDGET_ITEM]: budget?.bi_number,
    [budgetProcurementTypeConstants.PURCHASE_ORDER]: budget?.po_number,
  })[budget?.procurement_type] || null,
  /**
   * Returns a StatusMark component for the given budget, or null when no budget is provided.
   * @param {Object|null} budget - Budget object with a status property.
   * @returns {React.ReactElement|null} StatusMark component or null.
   * @example
   * budgetHelpers.getBudgetStatusMark({ status: 'active' });
   */
  getBudgetStatusMark: (budget) => {
    if (!budget) return null;
    return (
      <StatusMark
        statusCode={budget.status}
        statuses={budgetStatuses}
      />
    );
  },
  /**
   * Returns an Avatar.Group element listing up to 6 users with initials or logo avatars.
   * @param {Array<Object>} [users=[]] - User objects with full_name, email, and optional logo.
   * @param {Object} [groupProps={}] - Additional props spread onto the Avatar.Group component.
   * @returns {React.ReactElement|null} Avatar.Group or null if the list is empty.
   * @example
   * budgetHelpers.getUsersList([{ full_name: 'Jane Doe', logo: null }]);
   */
  getUsersList: (users = [], groupProps = {}) => {
    if (!users.length) return null;
    return <Avatar.Group className='d-flex' max={{ count: 6 }} size={22} {...groupProps}>
      {users.map((u, key) => {
        const logo = u.logo;
        const name = u.full_name || u.email;
        return (
          <Tooltip title={name} key={key}>
            <Avatar src={logo || undefined} style={{backgroundColor: logo ? 'unset' : '#BFBFBF'}}>
              {textHelpers.getInitials(name)}
            </Avatar>
          </Tooltip>
        )
      })}
    </Avatar.Group>
  },
  /**
   * Returns true when at least one approval step on the budget has already been decided (approved or rejected).
   * @param {Object|null} budget - Budget object with an approval array.
   * @returns {boolean} True if any approval step has a non-null is_approved value.
   * @example
   * budgetHelpers.hasBudgetApprovalDecision({ approval: [{ is_approved: true }] }); // true
   */
  hasBudgetApprovalDecision: (budget) => {
    return (budget?.approval || []).some((step) => step?.is_approved !== null);
  },
  /**
   * Returns true when the budget's values can still be edited: DRAFT, or IN_REVIEW as long as no approval step has been decided yet.
   * @param {Object|null} budget - Budget object with status and approval properties.
   * @returns {boolean} True if the budget is still editable.
   * @example
   * budgetHelpers.isEditableBudget({ status: 'in_review', approval: [{ is_approved: null }] }); // true
   */
  isEditableBudget: (budget) => {
    if (!budget) return false;
    if (budgetStatusConstants.DRAFT === budget.status) return true;
    if (budgetStatusConstants.IN_REVIEW === budget.status) {
      return !budgetHelpers.hasBudgetApprovalDecision(budget);
    }
    return false;
  },

  /**
   * Returns the list of procurement type select options with translated labels.
   * @param {Function} t - i18n translation function.
   * @returns {Array<{value: string, label: string}>} Procurement type options.
   * @example
   * budgetHelpers.getProcurementTypeOptions(t);
   */
  getProcurementTypeOptions: (t) => [
    {value: budgetProcurementTypeConstants.BUDGET_ITEM, label: t('budgetProcurementTypes.budget_item')},
    {value: budgetProcurementTypeConstants.PURCHASE_ORDER, label: t('budgetProcurementTypes.purchase_order')},
  ],
  /**
   * Returns sorted select options for a list of budgets, including a utilization tag in each label.
   * @param {Array<Object>} [budgets=[]] - Budget objects with id, name, utilized_amount, and limit_amount.
   * @returns {Array<{value: string, label: React.ReactElement}>} Select options sorted alphabetically by name.
   * @example
   * budgetHelpers.getBudgetOptions(budgets);
   */
  getBudgetOptions: (budgets = []) => {
    if (!budgets.length) return [];
    const sorted = [...budgets].sort((a, b) => a.name.localeCompare(b.name));
    return sorted.map(budget => ({
      value: budget.id,
      label: budgetHelpers.getBudgetWithUtilizationTag(budget)
    }));
  },
  /**
   * Returns a Space element with the budget name and a utilization percentage Tag.
   * @param {Object|null} budget - Budget object with name, utilized_amount, and limit_amount.
   * @param {React.ReactNode} [extraContent=null] - Optional node rendered before the tag, on the same side, with a 4px gap.
   * @returns {React.ReactElement|null} Space with name and tag, or null if no budget.
   * @example
   * budgetHelpers.getBudgetWithUtilizationTag({ name: 'Q1 Budget', utilized_amount: 500, limit_amount: 1000 });
   */
  getBudgetWithUtilizationTag: (budget, extraContent = null) => {
    if (!budget) return null;
    const utilization = ((budget?.utilized_amount || 0) / (budget?.limit_amount || 0) * 100).toFixed(0);
    const isOverUtilized = utilization > 100;
    const style = {
      color: isOverUtilized ? colorsStyles.functional_danger : colorsStyles.neutral_7,
      backgroundColor: isOverUtilized ? 'rgba(255,120,117,0.6)' : colorsStyles.neutral_2,
      borderRadius: 16,
      lineHeight: '20px',
      margin: 0,
      padding: '2px 8px'
    }
    const tag = <Tag bordered={false} style={style}>{utilization}%</Tag>;
    return (
      <Space size='small' align='center' className='w-100 justify-space-between'>
        <span>{budget.name}</span>
        {extraContent ? <Space size={4} align='center'>{extraContent}{tag}</Space> : tag}
      </Space>
    )
  },
  /**
   * Returns the utilization label element for the given budget ID, or the raw ID if not found.
   * @param {Array<Object>} [budgets=[]] - List of budget objects.
   * @param {string} budgetId - ID of the budget to look up.
   * @returns {React.ReactElement|string} Budget label element or raw budgetId string.
   * @example
   * budgetHelpers.getBudgetLabel(budgets, 'budget-123');
   */
  getBudgetLabel: (budgets = [], budgetId) => {
    const budget = budgets.find(budget => budget.id === budgetId);
    return budget ? budgetHelpers.getBudgetWithUtilizationTag(budget) : budgetId;
  },
  /**
   * Returns a NameWithLink component linking to the budget details page.
   * @param {Object|null} budget - Budget object with id and name properties.
   * @returns {React.ReactElement|null} NameWithLink element or null if no budget.
   * @example
   * budgetHelpers.getBudgetNameWithLink({ id: 'b1', name: 'Q2 Budget' });
   */
  getBudgetNameWithLink: (budget) => {
    if (!budget) return null;
    const name = budgetHelpers.getBudgetName(budget);
    return <NameWithLink name={name} url={`${routes.budgetsActive}/${budget.id}`} />;
  },
  /**
   * Calculates progress bar props and remaining budget info for a budget utilization display.
   * @param {Object} params - Parameters object.
   * @param {number} [params.totalSpent=0] - Total amount spent (invoiced + spent combined).
   * @param {number} [params.budgetLimit=0] - Budget limit amount.
   * @param {number} [params.spent=0] - Amount spent (used for the success sub-bar).
   * @param {boolean} [params.clearUtilization=false] - When true, adjusts remaining percentage for over-utilization display.
   * @returns {{remainingBudget: Object, progressProps: Object}} Progress props and remaining budget details.
   * @example
   * budgetHelpers.calculateBudgetProgress({ totalSpent: 800, budgetLimit: 1000, spent: 600 });
   */
  calculateBudgetProgress: ({totalSpent = 0, budgetLimit = 0, spent = 0, clearUtilization = false}) => {
    const percentage = budgetLimit ? (totalSpent / budgetLimit) * 100 : 0;
    const percentageSpent = budgetLimit ? (spent / budgetLimit) * 100 : 0;

    const progressProps = {
      percent: percentage > 100 ? 100 : percentage,
      success: {percent: percentageSpent, strokeColor: budgetSpendingData[budgetSpendingKeys.SPENT].color}
    }
    const remainingBudget = {
      amount: budgetLimit - totalSpent,
      percentage: clearUtilization ? percentage > 100 ? percentage - 100 : percentage : percentage,
      type: (budgetLimit - totalSpent) >= 0 ? 'positive' : 'negative'
    }
    return { remainingBudget, progressProps };
  },
  /**
   * Returns true when the budget's procurement type is PURCHASE_ORDER.
   * @param {Object} budget - Budget object with a procurement_type property.
   * @returns {boolean} True if the budget is a purchase order.
   * @example
   * budgetHelpers.isPurchaseOrderBudget({ procurement_type: 'purchase_order' }); // true
   */
  isPurchaseOrderBudget: (budget) => {
    return budget?.procurement_type === budgetProcurementTypeConstants.PURCHASE_ORDER;
  },
  /**
   * Returns true when the budget's status is DRAFT.
   * @param {Object} budget - Budget object with a status property.
   * @returns {boolean} True if the budget is in DRAFT status.
   * @example
   * budgetHelpers.isDraftBudget({ status: 'draft' }); // true
   */
  isDraftBudget: (budget) => {
    return budget?.status === budgetStatusConstants.DRAFT;
  },
  /**
   * Returns the Ant Design Table `scroll` prop shared by the Budgets requests/active pages,
   * accounting for the extra height the "Load more" button takes when it's enabled.
   * @param {Object} [params={}] - Parameters object.
   * @param {boolean} [params.isEnabled=false] - Whether the "Load more" button is currently shown.
   * @returns {{x: string, y: string}} Table scroll config.
   * @example
   * budgetHelpers.getTableScroll({ isEnabled: loadMoreProps.isEnabled });
   */
  getTableScroll: ({isEnabled = false} = {}) => ({
    x: 'max-content',
    y: `calc(100vh - ${isEnabled ? 273 : 241}px)`
  })
}
